import React, { ReactNode, useEffect, useState } from 'react';
import classNames from 'classnames';

import { IconMenu02, IconX } from '@pentabd/icons';

import MenuItem from './MenuItem';
import Text from '../text/Text';
import Skeleton from '../skeleton/Skeleton';

import useMediaQuery, { SCREEN_SIZES } from '../../hooks/useMediaQuery';
import useElementSize from '../../hooks/useElementSize';

export interface SidebarProps {
  collapse?: boolean;
  position?: 'left' | 'right';
  activeNavItem?: string;
  header?: {
    onClick?: () => void;
    link?: string;
    logo?: ReactNode;
    name?: string;
  };
  topComponent?: ReactNode;
  bodyTop?: {
    icon: ReactNode;
    name: string;
    subMenu?: {
      name: string;
      link?: string;
    }[];
    link?: string;
    disabled?: boolean;
    hide?: boolean;
  }[];
  bodyBottom?: {
    icon: ReactNode;
    name: string;
    link?: string;
    disabled?: boolean;
    hide?: boolean;
  }[];
  footer?: {
    link?: string;
    avatar?: ReactNode;
    title?: string;
    subtitle?: string;
    onClick?: () => void;
    icon?: ReactNode;
  };
  fixed?: boolean;
  extensions?: ReactNode;
  className?: string;
  bgColor?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Link: any;
  customClass?: {
    navItemColor?: string;
    navItemSelectedColor?: string;
    navItemHoverColor?: string;
    navItemTextColor?: string;
    footerColor?: string;
    footerBgColor?: string;
    footerBorderColor?: string;
  };
  loadingItemCount?: number;
  loading?: boolean;
  footerOptions?: {
    icon: ReactNode;
    name: string;
    link?: string;
    disabled?: boolean;
    hide?: boolean;
    onClick?: () => void;
  }[];
  testId?: string;
}

const Sidebar = ({
  collapse = false,
  activeNavItem,
  position = 'left',
  topComponent,
  header,
  bodyTop = [],
  footer,
  bodyBottom,
  Link,
  fixed,
  extensions,
  className,
  bgColor = 'bg-gradient-generic',
  customClass,
  loadingItemCount = 6,
  loading,
  footerOptions,
  testId,
}: SidebarProps) => {
  const [updateCollapse, setUpdateCollapse] = useState(collapse);
  useEffect(() => {
    setUpdateCollapse(!updateCollapse);
  }, [collapse]);
  const [headerRef, headerSize] = useElementSize();
  const [searchRef, searchSize] = useElementSize();
  const [footerRef, footerSize] = useElementSize(); // Changed from bottomRef to footerRef for clarity
  const isMobile = useMediaQuery(`(max-width: ${SCREEN_SIZES.MD})`);
  const [open, setOpen] = useState(false);
  const [navItemSelected, setNavItemSelected] = useState('');

  useEffect(() => {
    if (isMobile) {
      setOpen(false);
    }
  }, [isMobile]);

  useEffect(() => {
    if (activeNavItem) {
      setNavItemSelected(activeNavItem);
    }
  }, [activeNavItem]);

  const handleMenuClick = () => {
    setUpdateCollapse(true);
  };

  const handleSelected = (name: string, disabled: boolean) => {
    if (disabled !== true) {
      setNavItemSelected(name);
    }
  };

  const handleClick = () => {
    setOpen(!open);
  };

  const wrapperClass = classNames(
    'sidebar border-end border-start top-0',
    {
      'left-0': position === 'left',
      'right-0': position === 'right',
      'position-fixed': fixed,
      'position-relative': !fixed,
      'shadow-sm border-bottom vw-100 position-fixed z-6': isMobile,
      'w-auto vh-100': !isMobile,
    },
    className,
    bgColor,
  );

  return (
    <div className={wrapperClass} data-testid={testId}>
      {header ? (
        <div
          ref={headerRef}
          className="d-flex align-items-center justify-content-between w-100 overflow-hidden py-8 px-12 bg-transparent"
        >
          <Link
            to={header?.link}
            className="d-flex align-items-center text-ellipsis text-nowrap gap-6"
            onClick={header.onClick}
          >
            {header.logo ? (
              <span className="d-flex align-items-center justify-content-center pointer">
                {header.logo}
              </span>
            ) : null}
            {header.name ? (
              <Text
                weight="bold"
                className={classNames('pointer', {
                  'd-none': !isMobile && !updateCollapse,
                })}
              >
                {header.name}
              </Text>
            ) : null}
          </Link>
          <div
            onClick={handleClick}
            className={classNames('d-flex align-items-center pointer', {
              'd-none': !isMobile,
            })}
          >
            {!open ? (
              <IconMenu02 size="24" stroke="subtitle2" />
            ) : (
              <IconX size="24" stroke="subtitle2" />
            )}
          </div>
        </div>
      ) : null}

      <div
        className={classNames({
          'sidebar-from-side': isMobile,
          'sidebar-from-side-open w-100': isMobile && open,
          'd-block': !isMobile,
          'bg-white': isMobile,
        })}
        style={{
          top: `${headerSize.height}px`,
          height: `calc(100vh - ${headerSize.height}px)`,
        }}
      >
        <div
          className={classNames('h-100 d-flex flex-column', {
            [bgColor]: isMobile,
          })}
        >
          {topComponent && (
            <div
              ref={searchRef}
              className={classNames('px-6 pt-4 pb-10 shrink-0', {
                'd-none': !updateCollapse && !isMobile,
              })}
            >
              {topComponent}
            </div>
          )}

          {/* Main scrollable content area */}
          <div className="grow-1 overflow-hidden scrollbar">
            <div className="h-100 overflow-y-auto ">
              {bodyTop?.length ? (
                <div className="px-8">
                  {bodyTop?.map((item, index) => {
                    return !item?.hide ? (
                      <div
                        key={index}
                        className={classNames({
                          'opacity-50': item.disabled === true,
                        })}
                      >
                        {item?.subMenu ? (
                          <div
                            onClick={() => {
                              handleMenuClick(),
                                handleSelected(item.name, item.disabled);
                            }}
                            className={classNames('pt-2', {
                              'pointer-events-none': item.disabled === true,
                            })}
                          >
                            <MenuItem
                              icon={item.icon}
                              name={item.name}
                              submenu={item.subMenu}
                              collapse={updateCollapse}
                              isMobile={isMobile}
                              Link={Link}
                              onClickMenu={handleClick}
                              customClass={customClass}
                              navItemSelected={navItemSelected}
                              disabled={item.disabled}
                              testId={testId}
                            />
                          </div>
                        ) : item.link ? (
                          <div
                            onClick={() => {
                              isMobile && handleClick(),
                                handleSelected(item.name, item.disabled);
                            }}
                            className={classNames('pt-2', {
                              'pointer-events-none': item.disabled === true,
                            })}
                          >
                            <Link
                              to={item.link}
                              className="text-decoration-none"
                            >
                              <MenuItem
                                icon={item.icon}
                                name={item.name}
                                collapse={updateCollapse}
                                isMobile={isMobile}
                                Link={Link}
                                customClass={customClass}
                                navItemSelected={navItemSelected}
                                disabled={item.disabled}
                              />
                            </Link>
                          </div>
                        ) : null}
                      </div>
                    ) : null;
                  })}
                </div>
              ) : null}

              {loading
                ? Array(loadingItemCount)
                    .fill(0)
                    .map((_, i) => (
                      <Skeleton
                        key={i}
                        className="rounded-4 mx-8 my-4"
                        height="2.5rem"
                      />
                    ))
                : null}
            </div>
          </div>

          {/* Footer section - fixed at bottom */}
          <div
            ref={footerRef}
            className={classNames(
              'shrink-0 bg-transparent',
              customClass?.footerBgColor,
            )}
          >
            {updateCollapse && extensions ? (
              <div className="p-8">{extensions}</div>
            ) : null}

            {bodyBottom ? (
              <div className="px-8">
                {bodyBottom.map((item, index) =>
                  item.link && !item?.hide ? (
                    <div
                      key={index}
                      className={classNames('d-flex align-items-center pt-2')}
                      onClick={() => {
                        isMobile && handleClick(),
                          handleSelected(item.name, item.disabled);
                      }}
                    >
                      <Link
                        to={item.link}
                        className={classNames('text-decoration-none w-100', {
                          'pointer-events-none': item.disabled === true,
                        })}
                      >
                        <MenuItem
                          icon={item.icon}
                          name={item.name}
                          collapse={updateCollapse}
                          isMobile={isMobile}
                          Link={Link}
                          customClass={customClass}
                          navItemSelected={navItemSelected}
                          disabled={item.disabled}
                        />
                      </Link>
                    </div>
                  ) : null,
                )}
              </div>
            ) : null}

            {footer ? (
              <div
                className={classNames(
                  'd-flex flex-column pointer mt-12 mx-8 mb-16 py-4 bg-transparent',
                  customClass?.footerBorderColor,
                )}
              >
                {footerOptions ? (
                  <div className="mb-4">
                    {footerOptions.map((item, index) =>
                      item?.link && !item?.hide ? (
                        <div
                          key={index}
                          className={classNames(
                            'd-flex align-items-center pt-2',
                          )}
                          onClick={() => {
                            isMobile && handleClick(),
                              handleSelected(item.name, item.disabled);
                            !isMobile && item?.onClick && item?.onClick();
                          }}
                        >
                          <Link
                            to={item.link}
                            className={classNames(
                              'text-decoration-none w-100',
                              {
                                'pointer-events-none': item.disabled === true,
                              },
                            )}
                          >
                            <MenuItem
                              icon={item.icon}
                              name={item.name}
                              collapse={updateCollapse}
                              isMobile={isMobile}
                              Link={Link}
                              customClass={customClass}
                              navItemSelected={navItemSelected}
                              disabled={item.disabled}
                            />
                          </Link>
                        </div>
                      ) : null,
                    )}
                  </div>
                ) : null}

                <span className="d-flex gap-6 w-100">
                  {footer?.avatar ? (
                    <span className="d-flex justify-content-center align-items-center">
                      {footer?.avatar}
                    </span>
                  ) : null}

                  <div
                    className={classNames(
                      'd-flex justify-content-between align-items-center w-100',
                      { 'd-none': !updateCollapse && !isMobile },
                      customClass?.footerColor,
                    )}
                  >
                    <div className="d-flex flex-column">
                      {footer?.title ? (
                        <Text
                          size="sm"
                          weight="medium"
                          color={
                            customClass?.footerColor
                              ? customClass.footerColor
                              : 'dark'
                          }
                        >
                          {footer.title}
                        </Text>
                      ) : null}

                      {footer.subtitle ? (
                        <Text
                          size="sm"
                          color={
                            customClass?.footerColor
                              ? customClass.footerColor
                              : 'subtitle1'
                          }
                        >
                          {footer?.subtitle}
                        </Text>
                      ) : null}
                    </div>
                    <Link to={footer?.link} onClick={footer.onClick}>
                      {footer?.icon}
                    </Link>
                  </div>
                </span>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
